// Demo mock API: apiClient routes every request here instead of the network.
// Handlers return plain data (or a DemoResponse for non-200s); unmatched
// routes 404 with a console.warn so gaps are easy to spot during development.

import { DEMO_EMAIL, DEMO_PASSWORD, demoUser } from './config';
import { authHandlers } from './handlers/auth';
import { clientHandlers } from './handlers/clients';
import { erpHandlers } from './handlers/erp';
import { invoiceHandlers } from './handlers/invoices';
import { miscHandlers } from './handlers/misc';
import { reconciliationHandlers } from './handlers/reconciliation';
import { reportHandlers } from './handlers/reports';

export interface DemoRequest {
  method: string;
  /** Path relative to /api/v1, e.g. "/clients/abc" (no query string). */
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
  body: unknown;
}

export interface DemoResponse {
  status: number;
  json: unknown;
}

export type DemoHandler = (
  req: DemoRequest
) => unknown | DemoResponse | Response | Promise<unknown | DemoResponse | Response>;

export interface DemoRoute {
  method: string;
  /** Path pattern with :params, e.g. "/invoices/:id/extracted-data". */
  pattern: string;
  handler: DemoHandler;
}

export function route(method: string, pattern: string, handler: DemoHandler): DemoRoute {
  return { method, pattern, handler };
}

export function error(status: number, detail: string): DemoResponse {
  return { status, json: { detail } };
}

function isDemoResponse(value: unknown): value is DemoResponse {
  return !!value && typeof value === 'object' && 'status' in value && 'json' in value;
}

function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) {
      params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
    } else if (patternParts[i] !== pathParts[i]) {
      return null;
    }
  }
  return params;
}

const routes: DemoRoute[] = [
  ...authHandlers,
  ...clientHandlers,
  ...erpHandlers,
  ...invoiceHandlers,
  ...reconciliationHandlers,
  ...reportHandlers,
  ...miscHandlers,
];

const LATENCY_MS: [number, number] = [120, 350];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Drop-in replacement for fetch() used by ApiClient in demo mode. */
export async function demoFetch(fullUrl: string, options: RequestInit = {}): Promise<Response> {
  const url = new URL(fullUrl, window.location.origin);
  const path = url.pathname.replace(/^.*\/api\/v1/, '') || '/';
  const method = (options.method ?? 'GET').toUpperCase();

  let body: unknown;
  if (typeof options.body === 'string') {
    try {
      body = JSON.parse(options.body);
    } catch {
      body = options.body;
    }
  } else {
    body = options.body; // FormData or undefined
  }

  await sleep(LATENCY_MS[0] + Math.random() * (LATENCY_MS[1] - LATENCY_MS[0]));

  for (const r of routes) {
    if (r.method !== method) continue;
    const params = matchPattern(r.pattern, path);
    if (!params) continue;
    const result = await r.handler({ method, path, params, query: url.searchParams, body });
    if (result instanceof Response) return result; // e.g. PDF downloads
    const { status, json } = isDemoResponse(result) ? result : { status: 200, json: result };
    return new Response(JSON.stringify(json ?? {}), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  console.warn(`[demo] unhandled route: ${method} ${path}`);
  return new Response(JSON.stringify({ detail: `Demo route not implemented: ${method} ${path}` }), {
    status: 404,
    headers: { 'Content-Type': 'application/json' },
  });
}

export { DEMO_EMAIL, DEMO_PASSWORD, demoUser };
